import {useEffect, useState} from 'react';
import {Box, Popover, Typography, type TypographyProps} from '@mui/material';
import {InputField} from '@/components/common';
import {fsCompactNameInputSx, fsCompactNameSx} from '@/pages/components/fs-report/fsColumnSx';

export type InlineEditableNameProps = {
    value: string;
    placeholder?: string;
    /** Open InputField and commit rename */
    editable?: boolean;
    /** aliasLocked=1: reserved; Adjustment UI no longer shows locked popover for leaf rename */
    locked?: boolean;
    /** Item label used in locked message: This {lockedItemLabel} can not be change! */
    lockedItemLabel?: string;
    typographySx?: TypographyProps['sx'];
    onCommit?: (next: string) => void | Promise<void>;
};

/**
 * Double-click to edit FS item display name (alias), or show locked popover.
 * Enter commits; Esc cancels; blur commits.
 */
export function InlineEditableName({
    value,
    placeholder,
    editable = false,
    locked = false,
    lockedItemLabel,
    typographySx,
    onCommit,
}: InlineEditableNameProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [lockAnchor, setLockAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!editing) setDraft(value);
    }, [value, editing]);

    const commit = async () => {
        const next = draft.trim();
        const prev = (value || '').trim();
        setEditing(false);
        if (next === prev || !onCommit) return;
        await onCommit(next);
    };

    const labelSx = {
        ...fsCompactNameSx,
        ...((typographySx as object) || {}),
    };

    if (editing && editable) {
        return (
            <InputField
                size="small"
                autoFocus
                fullWidth
                compact
                hiddenLabel
                margin="none"
                align="left"
                value={draft}
                placeholder={placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => {
                    void commit();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        void commit();
                    } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setDraft(value);
                        setEditing(false);
                    }
                }}
                // Same crushed Outlined density as FS MoneyField — keep adjustment row ~20px.
                sx={{...fsCompactNameInputSx, mr: 1}}
            />
        );
    }

    if (locked) {
        const itemLabel = (lockedItemLabel || value || 'item').trim() || 'item';
        return (
            <>
                <Typography
                    variant="body2"
                    title="This item is locked"
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        setLockAnchor(e.currentTarget);
                    }}
                    sx={{
                        ...labelSx,
                        cursor: 'default',
                        borderRadius: 0.5,
                        '&:hover': {backgroundColor: 'action.hover'},
                    }}
                >
                    {value}
                </Typography>
                <Popover
                    open={Boolean(lockAnchor)}
                    anchorEl={lockAnchor}
                    onClose={() => setLockAnchor(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                    transformOrigin={{vertical: 'top', horizontal: 'left'}}
                >
                    <Box sx={{p: 1.5, maxWidth: 320}}>
                        <Typography variant="body2">
                            {`This ${itemLabel} can not be change!`}
                        </Typography>
                    </Box>
                </Popover>
            </>
        );
    }

    if (!editable) {
        return (
            <Typography variant="body2" sx={labelSx}>
                {value}
            </Typography>
        );
    }

    return (
        <Typography
            variant="body2"
            title="Double-click to rename"
            onDoubleClick={(e) => {
                e.stopPropagation();
                setDraft(value);
                setEditing(true);
            }}
            sx={{
                ...labelSx,
                cursor: 'text',
                borderRadius: 0.5,
                '&:hover': {backgroundColor: 'action.hover'},
            }}
        >
            {value}
        </Typography>
    );
}

export function canEditItemAlias(item: {
    level?: number | null;
    aliasLocked?: number | null;
    children?: unknown[] | null;
    code?: string | null;
    fsId?: number | null;
}): boolean {
    const level = item.level ?? 0;
    // Headers / section titles (level<=2) stay fixed; leaf items may rename via company alias
    // even when master template has alias_locked=1 (e.g. Sales income → Trading income).
    if (level <= 2) return false;
    if (Array.isArray(item.children) && item.children.length > 0) return false;
    return Boolean(item.code && item.fsId);
}

/**
 * Adjustment UI no longer blocks leaf rename on alias_locked.
 * Locked popover is unused for rename; keep helper for callers that still pass `locked`.
 */
export function isItemAliasLocked(_item: {
    aliasLocked?: number | null;
    code?: string | null;
    fsId?: number | null;
}): boolean {
    return false;
}
